import { coarsePointer, finePointer, isBotOrHeadless, isLowPowerDevice, prefersReducedMotion } from '../core/env'
import { typewrite } from '../core/text'
import { $, $$ } from './dom'

// Boot log shown while loading. Steps unlock by progress; `floors` shows a live counter.
const BOOT_LOG = [
  { at: 0, text: 'boot craft-tower' },
  { at: 0.16, text: 'load fonts' },
  { at: 0.34, text: 'compile shaders' },
  { at: 0.52, text: 'stack floors', floors: true },
  { at: 0.9, text: 'light the lantern' },
]

/**
 * Loading screen: a lighthouse builds floor by floor while a boot log runs, then the
 * lantern lights up and the screen lifts away. Holds until fonts and the first WebGL frames are ready.
 */
export async function runPreloader(ready: Promise<unknown>): Promise<void> {
  const root = $('#preloader')
  if (isBotOrHeadless() || prefersReducedMotion()) {
    root?.classList.add('is-done')
    document.documentElement.classList.add('is-loaded')
    root?.remove()
    return
  }

  const count = $('#preloader-count')
  const bar = $('#preloader-bar')
  const art = $('#preloader-art')
  const log = $('#preloader-log')
  const floors = $$('.pl-floor', art)
  const isMobile = coarsePointer || isLowPowerDevice()
  const minDuration = isMobile ? 650 : 850
  const start = performance.now()
  let done = false
  ready.finally(() => (done = true))

  let skipped = false
  const skip = () => {
    if (skipped) return
    skipped = true
    done = true
  }
  root?.addEventListener('click', skip, { once: true })
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') skip()
  }, { once: true })

  let logged = 0
  let floorStatus: HTMLElement | null = null
  const lines: HTMLElement[] = []
  const addLine = (text: string) => {
    lines.at(-1)?.classList.remove('is-active')
    const status = lines.at(-1)?.querySelector<HTMLElement>('.pl-status')
    if (status && status !== floorStatus) Object.assign(status, { textContent: 'ok', className: 'pl-status is-ok' })
    if (status && status === floorStatus) status.classList.add('is-ok')
    const li = document.createElement('li')
    li.className = 'is-active'
    li.innerHTML = '<span class="pl-prompt">›</span><span class="pl-text"></span><span class="pl-status"></span>'
    li.querySelector('.pl-text')!.textContent = text
    log.append(li)
    lines.push(li)
    if (lines.length > 5) lines.shift()?.remove()
    return li.querySelector<HTMLElement>('.pl-status')!
  }

  const render = (p: number) => {
    count.textContent = String(Math.round(p * 100)).padStart(3, '0')
    bar.style.transform = `scaleX(${p})`
    const built = Math.min(floors.length, Math.floor((p / 0.88) * floors.length))
    floors.forEach((floor, i) => floor.classList.toggle('is-built', i < built))
    art.classList.toggle('is-crowned', p >= 0.9)
    while (logged < BOOT_LOG.length && p >= BOOT_LOG[logged].at) {
      const step = BOOT_LOG[logged++]
      const status = addLine(step.text)
      if (step.floors) floorStatus = status
    }
    if (floorStatus) floorStatus.textContent = `${String(built).padStart(2, '0')}/${floors.length}`
  }

  await new Promise<void>((resolve) => {
    const tick = (now: number) => {
      if (skipped) {
        resolve()
        return
      }
      // Climb while loading (held at 90%). The moment the hero is ready, jump to 100% and lift.
      if (done) {
        resolve()
        return
      }
      const time = Math.min(1, (now - start) / Math.max(1, minDuration))
      render(Math.min(0.9, 1 - Math.pow(1 - time, 1.6)))
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  })

  render(1)
  addLine('ready')
  lines.at(-1)!.classList.remove('is-active')
  art.classList.add('is-lit')

  root.classList.add('is-done')
  document.documentElement.classList.add('is-loaded')
  window.setTimeout(() => root.remove(), 1000)
}

/** Live clock for Bar, Montenegro. */
export function startClock(getLocale: () => string): () => void {
  const el = $<HTMLTimeElement>('#clock')
  const update = () => {
    const now = new Date()
    const time = new Intl.DateTimeFormat(getLocale(), { timeZone: 'Europe/Podgorica', hour: '2-digit', minute: '2-digit', hour12: false }).format(now)
    const zone =
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Podgorica', timeZoneName: 'short' })
        .formatToParts(now)
        .find((part) => part.type === 'timeZoneName')?.value ?? ''
    el.textContent = `${time} ${zone}`
    el.dateTime = now.toISOString()
  }
  update()
  window.setInterval(update, 15_000)
  return update
}

/**
 * "GO CYBER, TRY ME!" — same behaviour as the current site: appears 2.5 s after load,
 * again every five minutes while Cyber mode is off, and hides itself after eight seconds.
 */
export class CyberHint {
  private el = $('#cyber-hint')
  private text = $('#cyber-hint-text')
  private showTimer = 0
  private hideTimer = 0
  private repeatTimer = 0
  private stopTyping: (() => void) | null = null

  constructor(
    private readonly getText: () => string,
    private readonly onVisibility: (visible: boolean) => void,
  ) {}

  schedule(): void {
    this.cancel()
    this.showTimer = window.setTimeout(() => this.show(), 2500)
    this.repeatTimer = window.setInterval(() => this.show(), 5 * 60 * 1000)
  }

  cancel(): void {
    window.clearTimeout(this.showTimer)
    window.clearInterval(this.repeatTimer)
    this.hide()
  }

  private show(): void {
    if (window.scrollY > window.innerHeight * 0.5) return
    this.el.hidden = false
    this.onVisibility(true)
    this.stopTyping?.()
    this.stopTyping = typewrite(this.text, this.getText(), 85)
    window.clearTimeout(this.hideTimer)
    this.hideTimer = window.setTimeout(() => this.hide(), 8000)
  }

  private hide(): void {
    window.clearTimeout(this.hideTimer)
    this.stopTyping?.()
    this.stopTyping = null
    if (!this.el.hidden) {
      this.el.hidden = true
      this.onVisibility(false)
    }
  }
}

/** Magnetic buttons and the cursor spotlight on cards (fine pointers only). */
export function initPointerEffects(): void {
  if (!finePointer || prefersReducedMotion()) return

  document.addEventListener('pointermove', (event) => {
    const card = (event.target as HTMLElement).closest<HTMLElement>('[data-spotlight]')
    if (!card) return
    const r = card.getBoundingClientRect()
    card.style.setProperty('--mx', `${event.clientX - r.left}px`)
    card.style.setProperty('--my', `${event.clientY - r.top}px`)
  })

  const bindMagnet = (el: HTMLElement) => {
    if (el.dataset.magnetBound) return
    el.dataset.magnetBound = '1'
    el.addEventListener('pointermove', (event) => {
      const r = el.getBoundingClientRect()
      const x = (event.clientX - (r.left + r.width / 2)) * 0.22
      const y = (event.clientY - (r.top + r.height / 2)) * 0.3
      el.style.transform = `translate(${x}px, ${y}px)`
    })
    el.addEventListener('pointerleave', () => (el.style.transform = ''))
  }
  $$('[data-magnetic]').forEach(bindMagnet)
}

/** Scale the footer wordmark so it spans the container exactly without forced layout thrashing. */
export function fitWordmark(): void {
  const el = $('#wordmark')
  if (!el || !el.parentElement) return
  const host = el.parentElement
  const hostWidth = host.clientWidth
  if (!hostWidth) return

  // Measure without pre-mutating el.style.fontSize to avoid triggering a synchronous forced reflow!
  const width = el.getBoundingClientRect().width
  if (width <= 0) return

  const currentSize = parseFloat(window.getComputedStyle(el).fontSize) || 100
  const targetSize = (currentSize * hostWidth) / width

  // Only apply update when the difference is perceptible (> 0.25px) to prevent layout dirtying
  if (Math.abs(currentSize - targetSize) > 0.25) {
    el.style.fontSize = `${targetSize}px`
  }
}
